import React, { useEffect, useMemo, useRef, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";
import { Building2, MapPin, Globe, Search, Loader2, ChevronDown, Check } from "lucide-react";
import { api } from "@/lib/api";

/**
 * Searchable dropdown backed by an async fetcher `fetchOptions(q) -> [{value,label,...}]`.
 * Renders a Popover with an input + result list. `renderOption` is optional custom row.
 */
export function SearchSelect({
  value,
  placeholder,
  disabled = false,
  onPick,           // (option) => void
  fetchOptions,     // (q) => Promise<Array<{value,label,...}>>
  emptyLabel = "No results",
  minChars = 0,
  icon = <ChevronDown size={14} className="text-slate-400" />,
  testid = "search-select",
  optionKey = (o) => o.value,
  renderOption,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [opts, setOpts] = useState([]);
  const [loading, setLoading] = useState(false);
  const debRef = useRef();

  useEffect(() => {
    if (!open) return;
    clearTimeout(debRef.current);
    const query = q.trim();
    if (query.length < minChars) { setOpts([]); return; }
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const list = await fetchOptions(query);
        setOpts(Array.isArray(list) ? list : []);
      } catch {
        setOpts([]);
      } finally {
        setLoading(false);
      }
    }, 200);
    return () => clearTimeout(debRef.current);
  }, [q, open]); // eslint-disable-line

  const label = value?.label || value?.name || value || "";

  return (
    <Popover open={open} onOpenChange={(o) => { if (!disabled) { setOpen(o); if (o) setQ(""); } }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          className={`w-full border rounded px-3 py-2 text-sm text-left flex items-center gap-2 transition
            ${disabled ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                       : "bg-white hover:border-slate-400 border-border focus:outline-none focus:ring-2 focus:ring-emerald-500/30"}`}
        >
          {icon}
          <span className={`flex-1 truncate ${label ? "text-slate-900" : "text-slate-400"}`}>
            {label || placeholder || "Select…"}
          </span>
          <ChevronDown size={12} className="text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-[280px] p-0 overflow-hidden" data-testid={`${testid}-popover`}>
        <div className="p-2 border-b border-slate-100">
          <input
            autoFocus
            data-testid={`${testid}-input`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`Type to search${minChars > 0 ? ` (min ${minChars} chars)` : ""}`}
            className="w-full border border-slate-200 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>
        <div className="max-h-[280px] overflow-y-auto">
          {loading && <div className="px-3 py-2 text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Loading…</div>}
          {!loading && q.trim().length < minChars && (
            <div className="px-3 py-2 text-xs text-slate-400">Type at least {minChars} character{minChars > 1 ? "s" : ""}</div>
          )}
          {!loading && opts.length === 0 && q.trim().length >= minChars && (
            <div className="px-3 py-2 text-xs text-slate-400">{emptyLabel}</div>
          )}
          {opts.map((o) => (
            <button
              key={optionKey(o)}
              type="button"
              data-testid={`${testid}-option-${optionKey(o)}`}
              onClick={() => { onPick(o); setOpen(false); setQ(""); }}
              className="w-full text-left px-3 py-2 hover:bg-emerald-50 border-b border-slate-50 last:border-b-0 text-sm text-slate-900"
            >
              {renderOption ? renderOption(o) : (o.label || o.name || String(o.value))}
            </button>
          ))}
        </div>
      </PopoverContent>
    </Popover>
  );
}

// ---------- Data lists ----------
export const ROOM_CATEGORIES = [
  "Standard Room", "Superior Room", "Deluxe Room", "Premium Room",
  "Executive Room", "Junior Suite", "Suite", "Family Room",
  "Connecting Room", "Villa",
];

export const MEAL_PLANS = [
  { code: "RO", label: "Room Only (RO)" },
  { code: "BB", label: "Bed & Breakfast (BB)" },
  { code: "HB", label: "Half Board (HB)" },
  { code: "FB", label: "Full Board (FB)" },
  { code: "AI", label: "All Inclusive (AI)" },
];

// Cache countries at module scope so all hotel forms share one fetch
let _countriesCache = null;
async function fetchAllCountries() {
  if (_countriesCache) return _countriesCache;
  const { data } = await api.get("/catalog/countries");
  _countriesCache = (data || []).map((c) => ({ value: c.code, label: c.name }));
  return _countriesCache;
}

/**
 * Cascading Hotel Details form for one hotel row.
 * Country → City → Google Hotel search → Room Category → Meal Plan.
 * Changing an upstream field clears all downstream selections.
 */
export default function HotelForm({ hotel, onChange, onRemove, index, dateFieldsDisabled = false, DateFieldsSlot, RoomsNightsSlot, CategorySlot }) {
  const {
    country_code = "", country = "", city = "",
    name = "", address = "", image_url = "", hotel_place_id = "",
    room_category = "", meal_plan = "",
  } = hotel;

  // Local UI state for the "Other" free-text on room/meal
  const isCustomRoom = room_category && !ROOM_CATEGORIES.includes(room_category);
  const isCustomMeal = meal_plan && !MEAL_PLANS.some((m) => m.label === meal_plan || m.code === meal_plan);
  const [roomOther, setRoomOther] = useState(isCustomRoom);
  const [mealOther, setMealOther] = useState(isCustomMeal);

  const patch = (delta) => onChange({ ...hotel, ...delta });

  // Reset cascades
  const setCountry = (opt) => {
    patch({
      country_code: opt.value, country: opt.label,
      city: "", name: "", address: "", image_url: "", hotel_place_id: "",
      room_category: "", meal_plan: "",
    });
    setRoomOther(false); setMealOther(false);
  };
  const setCity = (opt) => {
    patch({
      city: opt.label,
      name: "", address: "", image_url: "", hotel_place_id: "",
      room_category: "", meal_plan: "",
    });
    setRoomOther(false); setMealOther(false);
  };
  const setHotel = async (h) => {
    // h from HotelSearchList (has place_id). Auto-fetch details to get city+country too.
    try {
      const { data } = await api.get("/hotels/place-details", { params: { place_id: h.place_id } });
      patch({
        hotel_place_id: data.place_id,
        name: data.name || h.name,
        address: data.address || h.address || "",
        image_url: data.image || h.image || "",
        city: data.city || city,
        country: data.country || country,
        rating: h.rating || 0,
      });
    } catch {
      patch({
        hotel_place_id: h.place_id,
        name: h.name, address: h.address || "", image_url: h.image || "",
        rating: h.rating || 0,
      });
    }
  };

  // Fetchers for each cascading select
  const fetchCountries = async (q) => {
    const list = await fetchAllCountries();
    const ql = q.trim().toLowerCase();
    if (!ql) return list.slice(0, 60);
    const starts = list.filter((c) => c.label.toLowerCase().startsWith(ql));
    const startsSet = new Set(starts.map((s) => s.value));
    const contains = list.filter((c) => !startsSet.has(c.value) && c.label.toLowerCase().includes(ql));
    return [...starts, ...contains].slice(0, 60);
  };
  const fetchCities = async (q) => {
    if (!country_code) return [];
    const { data } = await api.get("/catalog/cities-by-code", { params: { country_code, q, limit: 40 } });
    return (data || []).map((name) => ({ value: name, label: name }));
  };

  const countryValue = useMemo(() => country ? { value: country_code, label: country } : null, [country, country_code]);
  const cityValue = useMemo(() => city ? { value: city, label: city } : null, [city]);

  return (
    <div className="border border-border rounded p-4 space-y-4">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          {image_url ? (
            <img src={image_url} alt="" className="w-14 h-10 object-cover rounded" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          ) : (
            <div className="w-14 h-10 rounded bg-slate-100 flex items-center justify-center text-slate-400"><Building2 size={14} /></div>
          )}
          <div className="overline text-[10px]">
            Hotel {index + 1}
            {hotel_place_id && <span className="ml-2 text-emerald-700 normal-case tracking-normal text-[10px]">✓ Google-linked</span>}
          </div>
        </div>
        <button data-testid={`hotel-remove-${index}`} onClick={onRemove} className="text-red-500 hover:text-red-700 text-xs">Remove</button>
      </div>

      {/* Step 1 & 2: Country → City */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">1. Country</label>
          <SearchSelect
            testid={`hotel-country-${index}`}
            value={countryValue}
            placeholder="Select country…"
            fetchOptions={fetchCountries}
            icon={<Globe size={14} className="text-emerald-600 shrink-0" />}
            onPick={setCountry}
          />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">
            2. City {!country_code && <span className="normal-case tracking-normal text-slate-400">(select country first)</span>}
          </label>
          <SearchSelect
            testid={`hotel-city-${index}`}
            value={cityValue}
            disabled={!country_code}
            placeholder={country_code ? "Select city…" : "—"}
            fetchOptions={fetchCities}
            icon={<MapPin size={14} className="text-emerald-600 shrink-0" />}
            onPick={setCity}
          />
        </div>
      </div>

      {/* Step 3: Hotel search via Google Places */}
      <div>
        <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">
          3. Hotel {!city && <span className="normal-case tracking-normal text-slate-400">(select city first)</span>}
        </label>
        <HotelSearchList
          disabled={!city}
          city={city}
          country={country}
          currentName={name}
          onPick={setHotel}
          testid={`hotel-picker-${index}`}
        />
        {name && (
          <div className="mt-2 bg-emerald-50/50 border border-emerald-100 rounded px-3 py-2 text-xs text-slate-700 flex items-start gap-3">
            {image_url && <img src={image_url} alt="" className="w-16 h-12 rounded object-cover shrink-0" onError={(e) => { e.currentTarget.style.display = "none"; }} />}
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-900 truncate">{name}</div>
              {address && <div className="text-slate-500 truncate">{address}</div>}
              <div className="text-slate-500">{city}{country ? `, ${country}` : ""}</div>
            </div>
          </div>
        )}
      </div>

      {/* Optional slots (dates, category, rooms/nights) supplied by parent */}
      {CategorySlot}
      {DateFieldsSlot}
      {RoomsNightsSlot}

      {/* Step 4: Room Category */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">4. Room Category</label>
          {roomOther ? (
            <div className="flex gap-2">
              <input
                data-testid={`hotel-room-other-${index}`}
                value={room_category}
                onChange={(e) => patch({ room_category: e.target.value })}
                placeholder="Custom room category"
                className="flex-1 border border-border rounded px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => { setRoomOther(false); patch({ room_category: "" }); }}
                className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
              >back to list</button>
            </div>
          ) : (
            <SearchSelect
              testid={`hotel-room-${index}`}
              value={room_category ? { value: room_category, label: room_category } : null}
              placeholder="Select room category…"
              fetchOptions={async (q) => {
                const options = [...ROOM_CATEGORIES.map((r) => ({ value: r, label: r })), { value: "__other__", label: "Other (custom)…" }];
                const ql = q.trim().toLowerCase();
                return ql ? options.filter((o) => o.label.toLowerCase().includes(ql)) : options;
              }}
              icon={<Building2 size={14} className="text-emerald-600 shrink-0" />}
              onPick={(o) => {
                if (o.value === "__other__") { setRoomOther(true); patch({ room_category: "" }); }
                else patch({ room_category: o.value });
              }}
            />
          )}
        </div>

        {/* Step 5: Meal Plan */}
        <div>
          <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">5. Meal Plan</label>
          {mealOther ? (
            <div className="flex gap-2">
              <input
                data-testid={`hotel-meal-other-${index}`}
                value={meal_plan}
                onChange={(e) => patch({ meal_plan: e.target.value })}
                placeholder="Custom meal plan"
                className="flex-1 border border-border rounded px-3 py-2 text-sm"
              />
              <button
                type="button"
                onClick={() => { setMealOther(false); patch({ meal_plan: "" }); }}
                className="text-xs text-slate-500 hover:text-slate-800 underline underline-offset-2"
              >back to list</button>
            </div>
          ) : (
            <SearchSelect
              testid={`hotel-meal-${index}`}
              value={meal_plan ? { value: meal_plan, label: meal_plan } : null}
              placeholder="Select meal plan…"
              fetchOptions={async (q) => {
                const options = [...MEAL_PLANS.map((m) => ({ value: m.label, label: m.label })), { value: "__other__", label: "Other (custom)…" }];
                const ql = q.trim().toLowerCase();
                return ql ? options.filter((o) => o.label.toLowerCase().includes(ql)) : options;
              }}
              icon={<Check size={14} className="text-emerald-600 shrink-0" />}
              onPick={(o) => {
                if (o.value === "__other__") { setMealOther(true); patch({ meal_plan: "" }); }
                else patch({ meal_plan: o.value });
              }}
            />
          )}
        </div>
      </div>
    </div>
  );
}

export function HotelSearchList({ disabled, city, country, currentName, onPick, testid }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState(currentName || "");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pickingId, setPickingId] = useState("");
  const debRef = useRef();

  useEffect(() => {
    if (!open || disabled) return;
    clearTimeout(debRef.current);
    debRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const term = q.trim() || "hotels";
        const { data } = await api.get("/hotels/places-search", {
          params: { q: term, city, limit: 10 },
        });
        setResults(Array.isArray(data) ? data : []);
      } catch { setResults([]); }
      finally { setLoading(false); }
    }, 250);
    return () => clearTimeout(debRef.current);
  }, [q, open, disabled, city]);

  const pick = async (h) => {
    setPickingId(h.place_id);
    try { await onPick(h); setOpen(false); }
    finally { setPickingId(""); }
  };

  return (
    <Popover open={open} onOpenChange={(o) => { if (!disabled) setOpen(o); }}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid={testid}
          disabled={disabled}
          className={`w-full border rounded px-3 py-2 text-sm text-left flex items-center gap-2 transition
            ${disabled ? "bg-slate-100 text-slate-400 cursor-not-allowed border-slate-200"
                       : "bg-white border-emerald-300 hover:border-emerald-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"}`}
        >
          <Building2 size={14} className="text-emerald-600 shrink-0" />
          <span className={`flex-1 truncate ${currentName ? "text-slate-900" : "text-slate-500"}`}>
            {currentName || (disabled ? "Select city first" : `Search hotels in ${city}${country ? `, ${country}` : ""}`)}
          </span>
          <Search size={12} className="text-slate-400 shrink-0" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[--radix-popover-trigger-width] min-w-[420px] p-0 overflow-hidden" data-testid={`${testid}-popover`}>
        <div className="p-2 border-b border-slate-100">
          <input
            autoFocus
            data-testid={`${testid}-input`}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={`e.g. Marriott, Taj Palace… (in ${city})`}
            className="w-full border border-slate-200 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
          />
        </div>
        <div className="max-h-[320px] overflow-y-auto">
          {loading && <div className="px-3 py-3 text-xs text-slate-400 flex items-center gap-2"><Loader2 size={12} className="animate-spin" /> Searching Google…</div>}
          {!loading && results.length === 0 && <div className="px-3 py-3 text-xs text-slate-400">No hotels found</div>}
          {results.map((h) => (
            <button
              key={h.place_id}
              type="button"
              data-testid={`${testid}-option-${h.place_id}`}
              disabled={!!pickingId}
              onClick={() => pick(h)}
              className={`w-full text-left px-3 py-2.5 flex items-start gap-3 border-b border-slate-50 last:border-b-0 ${pickingId === h.place_id ? "bg-emerald-50" : "hover:bg-slate-50"} disabled:opacity-60`}
            >
              <div className="w-14 h-11 shrink-0 rounded overflow-hidden bg-slate-100 flex items-center justify-center">
                {h.image ? (
                  <img src={h.image} alt="" className="w-full h-full object-cover" onError={(e) => { e.currentTarget.style.display = "none"; }} />
                ) : (
                  <Building2 size={14} className="text-slate-400" />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-sm text-slate-900 truncate font-medium">{h.name}</div>
                <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                  <MapPin size={10} /> {h.address}
                </div>
                {h.rating > 0 && (
                  <div className="text-[11px] text-amber-600 mt-0.5">★ {Number(h.rating).toFixed(1)} <span className="text-slate-400">({(h.review_count || 0).toLocaleString()} reviews)</span></div>
                )}
              </div>
              {pickingId === h.place_id && <Loader2 size={12} className="animate-spin text-emerald-600 mt-2" />}
            </button>
          ))}
        </div>
        <div className="px-3 py-2 text-[10px] text-slate-400 border-t border-slate-100 bg-slate-50/50">
          Results powered by Google Places
        </div>
      </PopoverContent>
    </Popover>
  );
}
